import api from "./api.service";
import { adminTokenService } from "./admin.token.service";

export interface CarrierServiceCountryData {
    id: number;
    isO2Country: string;
    minDays: number;
    maxDays: number;
}

export interface CarrierStore {
    nombre: string;
    zipcode: string;
}

export interface CarrierServiceItem {
    id: number;
    carrier: string;
    name: string;
    status: number;
    minDays: number | null;
    maxDays: number | null;
    rate: number | null;
    isPercent?: boolean;
    isFix?: boolean;
    rateType?: 'PERCENT' | 'FIX' | 'NONE';
    contact?: string;
    charge?: string;
    email?: string;
    phoneNumber?: string;
    isMain?: boolean;
    countryData?: CarrierServiceCountryData[];
    stores?: Record<string, CarrierStore[]>;
}

export interface GetCarrierServicesParams {
    page?: number;
    pageSize?: number;
    status?: number;
    SearchString?: string;
}

export interface GetCarrierServicesResponse {
    statusCode: number;
    message: string;
    data: CarrierServiceItem[];
    pagination: {
        totalCount: number;
        page: number;
        pageSize: number;
        totalPages: number;
    };
}

export interface UpdateCarrierServiceStatusData {
    IdCarrierService: number;
    Status: number;
}

export interface UpdateCarrierServiceData {
    IdCarrierService: number;
    rate?: number;
    isPercent?: boolean;
    isFix?: boolean;
    rateType?: 'PERCENT' | 'FIX';
    contact?: string;
    charge?: string;
    email?: string;
    phoneNumber?: string;
}

class AdminCarrierService {
    /**
     * Fetches a list of carrier services for administration purposes.
     * @param params Filtering and pagination parameters.
     * @returns A promise with the carrier services data and pagination info.
     */
    async getCarrierServices(params?: GetCarrierServicesParams): Promise<GetCarrierServicesResponse> {
        const token = adminTokenService.getAdminToken();
        try {
            const response = await api.get<GetCarrierServicesResponse>(
                "/admin/get-carrier-services",
                {
                    params,
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error fetching admin carrier services:", error);
            throw error;
        }
    }

    /**
     * Changes the status of a specific carrier service.
     * @param data Object containing the carrier service ID and the new status.
     * @returns A promise with the response.
     */
    async changeCarrierServiceStatus(data: UpdateCarrierServiceStatusData): Promise<{ statusCode: number, message: string }> {
        const token = adminTokenService.getAdminToken();
        try {
            const response = await api.post<{ statusCode: number, message: string }>(
                "/admin/change-carrier-service-status",
                data,
                {
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error updating carrier service status:", error);
            throw error;
        }
    }

    /**
     * Updates the delivery days for a specific carrier service.
     * @param data Object containing the carrier service ID, minDays and maxDays.
     * @returns A promise with the response.
     */
    async updateCarrierService(data: UpdateCarrierServiceData): Promise<{ statusCode: number, message: string }> {
        const token = adminTokenService.getAdminToken();
        try {
            const response = await api.post<{ statusCode: number, message: string }>(
                "/admin/update-carrier-service",
                data,
                {
                    headers: {
                        Authorization: `${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error updating carrier service:", error);
            throw error;
        }
    }
}

export const adminCarrierService = new AdminCarrierService();
